import { useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { FiUploadCloud } from "react-icons/fi";
import { z } from "zod";
import { Button } from "../../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { uploadInventory } from "../../../../helpers/mutations/targets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../../../components/ui/form";
import { FaFileZipper } from "react-icons/fa6";
import { useDropzone } from "react-dropzone";
import { Trash2 } from "lucide-react";
import { FaFileCsv } from "react-icons/fa6";
import {
  RadioGroup,
  RadioGroupItem,
} from "../../../../components/ui/radio-group";
import { Label } from "../../../../components/ui/label";
import TagsInput from "../../../../components/TagsInput";
import { Input } from "../../../../components/ui/input";
import { exportSampleFile } from "../../../../helpers/api";

const UploadInventoryModal = () => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("manual");

  const formSchema = useMemo(() => {
    if (type === "manual") {
      return z.object({
        name: z.string().min(1, "Name cannot be empty"),
        target: z.string().min(1, "Please add a target"),
        assetListFile: z
          .union([
            z.instanceof(File).optional(),
            z.string().nullable(),
            z.null(),
          ])
          .optional(),
      });
    }

    return z.object({
      name: z.string().optional(),
      target: z.string().optional(),
      assetListFile: z
        .instanceof(File, { message: "Please upload a valid file" })
        .refine((file) => file !== undefined && file !== null, {
          message: "File is required",
        }),
    });
  }, [type]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      target: "",
      assetListFile: null,
    },
  });

  const { control, setValue, watch } = form;

  const file = watch("assetListFile");

  const removeFile = () => {
    setValue("assetListFile", null);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "text/csv": [".csv"] },
    multiple: false,
    onDrop: (files) => {
      if (files.length > 0) {
        // onFileSelect(files[0]);
        setValue("assetListFile", files[0], { shouldValidate: true });
      }
    },
  });

  const [loadingExport, setLoadingExport] = useState(false);

  const handleDownloadSampleFile = async () => {
    setLoadingExport(true);
    try {
      const blob = await exportSampleFile();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sample_inventory.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download file", error);
      toast.error("Download failed");
    } finally {
      setLoadingExport(false);
    }
  };

  const queryClient = useQueryClient();

  const { mutate: upload, isPending: uploadLoading } = useMutation({
    mutationFn: uploadInventory,
    onSuccess: (data) => {
      toast(data?.message);
      queryClient.invalidateQueries({
        queryKey: ["inventory"],
      });
      form.reset();
      setOpen(false);
    },
    onError: (data) => {
      toast.error(data.message);
    },
  });

  const onSubmit = (data) => {
    if (type === "manual") {
      upload({
        name: data.name,
        asset_list: data.target,
      });
    } else {
      upload(file);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-fit px-2 rounded-lg" onClick={() => setOpen(true)}>
          <FaFileZipper />
          <span className="font-medium pr-2">Import inventory</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Import inventory
          </DialogTitle>
          <DialogDescription>
            Provide the website or IP address you'd like to monitor
          </DialogDescription>
        </DialogHeader>
        <hr />
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-1"
          >
            <div className="flex flex-col">
              <label
                htmlFor="inputType"
                className="text-sm font-medium text-neutral1000 mb-3"
              >
                Host Input Type
              </label>
              <RadioGroup
                // defaultValue="manual"
                value={type}
                className="flex items-center gap-12 mb-2"
                onValueChange={(value) => setType(value)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="manual"
                    id="r1"
                    // disabled={!assetType}
                  />
                  <Label htmlFor="r1" className="m-0">
                    Manual Entry
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem
                    value="upload"
                    id="r2"
                    // disabled={!assetType}
                  />
                  <Label htmlFor="r2" className="m-0">
                    File Upload
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {type === "manual" && (
              <>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter name" {...field} />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="target"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Domain(s) or IP Address(es)</FormLabel>
                      <TagsInput
                        // value={field.value || []}
                        // onChange={field.onChange}
                        value={field.value ? field.value.split(",") : []} // show as array
                        onChange={(tags) => field.onChange(tags.join(","))} // save as string
                        placeholder={
                          // assetType === "web"
                          //   ? "https://example.com, http://example.com, https://example.com/login.php"
                          //   : "E.g 192.168.8.1, 192.168.8.2, target.com"
                          "E.g 192.168.8.1, 192.168.8.2, target.com"
                        }
                      />
                      <FormMessage /> {/* Will now show the message */}
                    </FormItem>
                  )}
                />
              </>
            )}
            {type === "upload" && (
              <>
                <div className="w-full">
                  <Button
                    className="text-blue-500 p-0 mb-3"
                    variant={"ghost"}
                    loading={loadingExport}
                    onClick={handleDownloadSampleFile}
                  >
                    <FaFileZipper /> Download sample file
                  </Button>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload a .csv file containing your IPs/domains.{" "}
                    <span className="text-red-500">*</span>
                  </label>

                  <div className="border border-[#3636361A] rounded-lg p-6">
                    {!file ? (
                      <FormField
                        control={control}
                        name="assetListFile"
                        render={() => (
                          <>
                            <FormItem>
                              <div
                                {...getRootProps()}
                                className="flex flex-col items-center justify-center  cursor-pointer text-center bg-white hover:border-gray-400 transition-colors"
                              >
                                <input
                                  {...getInputProps()}
                                  // onChange={(e) => {
                                  //   onChange(e.target.files?.[0]); // correctly set the file object
                                  // }}
                                  // {...rest}
                                />

                                <FiUploadCloud className="text-2xl mb-2 text-[#B7B7B9]" />

                                <p className="text-sm text-neutral900 font-medium mt-3">
                                  Choose a file or drag & drop it here
                                </p>
                                <p className="text-xs text-neutral800 mt-1">
                                  Please upload a .csv file
                                </p>
                                {/* <p className="text-xs text-neutral800 mt-1 ">
                                Each entry should be separated by a comma.
                              </p>
                              <p className="text-xs text-neutral800 mt-1 mb-4">
                                {assetType === "web"
                                  ? "https://example.com, http://example.com, https://example.com/login.php"
                                  : "E.g 192.168.8.1, 192.168.8.2, target.com"}
                              </p> */}

                                <p className="text-neutral900 px-4 py-2 text-sm font-medium border rounded-lg ">
                                  Browse file
                                </p>
                              </div>
                            </FormItem>
                            <FormMessage />
                          </>
                        )}
                      />
                    ) : (
                      <div className="flex items-center justify-between bg-white">
                        <div className="flex items-center gap-3">
                          <FaFileCsv className="text-[32px] text-blue-950" />
                          <div>
                            <p className="font-medium text-sm text-gray-800">
                              {file.name}
                            </p>
                          </div>
                        </div>

                        <Trash2
                          onClick={removeFile}
                          className="text-gray-500 hover:text-red-600 transition"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white"
                loading={uploadLoading}
              >
                Import
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadInventoryModal;
